import React, { useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { Globe, Users, Award, Calendar, BookOpen, ExternalLink, Sparkles, CheckCircle2, Monitor } from 'lucide-react';

export default function VJDEPThContent() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-5" style={{ background: '#f8fafc', minHeight: '85vh' }}>
      <Helmet>
        <title>VJ-DEPTh Distance Education in Theology - Vidyajyoti College</title>
        <meta name="description" content="Vidyajyoti Distance Education Programme in Theology (VJ-DEPTh) - Empowering Catholic Laity worldwide." />
        <meta name="keywords" content="VJDEPTh, distance theology, lay theology India, Catholic distance education, online theology diploma" />
        <link rel="canonical" href="/academics/depth" />
      </Helmet>

      <div className="container">
        {/* Hero Section */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 mb-5" style={{ background: 'linear-gradient(135deg, #79031d 0%, #a31433 100%)', color: '#fff' }}>
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(255, 255, 255, 0.15)', color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>
                <Sparkles size={14} /> Distance Learning Initiative
              </div>
              <h1 className="display-6 fw-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                VJ-DEPTh: Outgoing Theology
              </h1>
              <h5 className="text-light fw-normal mb-3" style={{ opacity: 0.95 }}>
                Vidyajyoti Distance Education Programme in Theology
              </h5>
              <p className="lead mb-4 text-light" style={{ fontSize: '1.02rem', lineHeight: '1.7', opacity: 0.9 }}>
                Keeping in line with Pope Francis’s apostolic call for a “Church Going Forth”, Vidyajyoti equips lay faithful with rigorous biblical, doctrinal, and pastoral theology for transformative leadership in Church and society.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a
                  href="https://www.vjdepth.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn px-4 py-2.5 rounded-pill fw-semibold shadow-sm d-inline-flex align-items-center gap-2"
                  style={{ background: '#c69214', color: '#1a2b56', border: 'none' }}
                >
                  <Globe size={18} /> Visit VJ-DEPTh Portal (www.vjdepth.in) <ExternalLink size={14} />
                </a>
              </div>
            </div>

            <div className="col-lg-4 text-center mt-4 mt-lg-0">
              <div className="p-4 rounded-4" style={{ background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <Users size={40} className="mb-2 text-warning" />
                <h3 className="fw-bold text-white mb-0">400+</h3>
                <p className="small text-light opacity-90 mb-3">Enrolled Global Students</p>
                <div className="d-flex justify-content-around text-start border-top pt-3" style={{ borderColor: 'rgba(255,255,255,0.2)' }}>
                  <div>
                    <h5 className="fw-bold mb-0 text-white">200+</h5>
                    <small className="text-light opacity-75">Graduates</small>
                  </div>
                  <div>
                    <h5 className="fw-bold mb-0 text-white">6+</h5>
                    <small className="text-light opacity-75">Countries</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overview & Mission */}
        <div className="row g-4 mb-5">
          <div className="col-lg-7">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white">
              <h3 className="fw-bold mb-3" style={{ color: '#1a2b56' }}>Formation of Catholic Laity</h3>
              <p className="text-muted" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                Conceived originally by Dr. John Misquitta SJ as &ldquo;Do It Yourself Theology&rdquo; and later integrated into Vidyajyoti by Dr. George Mlakuzhiyil SJ, the current format of VJ-DEPTh represents a modernized, learner-friendly curriculum updated by Dr. P.T. Mathew SJ and Prof. Rajkumar Joseph SJ. Under the directorship of <strong>Dr. Fr. Anil D&rsquo;Almeida SJ</strong>, the programme has expanded across India, Malaysia, Dubai, Mauritius, Zurich, and Sri Lanka.
              </p>
              <p className="text-muted" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                Graduates actively serve as parish catechists, youth animators, pastoral council leaders, and coordinators for synodal webinars. VJ-DEPTh fulfills the vision of <em>Veritatis Gaudium</em> and <em>Apostolicam Actuositatem</em> by forming men and women to witness, evangelize, and minister in their local communities.
              </p>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4 p-md-5" style={{ background: '#1a2b56', color: '#fff' }}>
              <h4 className="fw-bold mb-4 text-warning">Programme Snapshot</h4>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="p-2 rounded-circle" style={{ background: 'rgba(255,255,255,0.1)' }}>
                    <Monitor size={20} className="text-warning" />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">Alternate Sundays Online</h6>
                    <small className="text-light opacity-75">5:00 PM – 7:00 PM IST Interactive Lectures</small>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-3">
                  <div className="p-2 rounded-circle" style={{ background: 'rgba(255,255,255,0.1)' }}>
                    <Globe size={20} className="text-warning" />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">Regional Contact Centres</h6>
                    <small className="text-light opacity-75">Face-to-face sessions and regional Eucharistic fellowship</small>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-3">
                  <div className="p-2 rounded-circle" style={{ background: 'rgba(255,255,255,0.1)' }}>
                    <Award size={20} className="text-warning" />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">Diploma in Theology</h6>
                    <small className="text-light opacity-75">Annual Convocation &amp; Certificates Awarded</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Prominent Features Grid */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white">
          <div className="text-center mx-auto mb-4" style={{ maxWidth: '600px' }}>
            <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(198,146,20,0.15)', color: '#8a6200', fontWeight: 600 }}>Key Advantages</span>
            <h3 className="fw-bold" style={{ color: '#1a2b56' }}>Prominent Features of VJ-DEPTh</h3>
          </div>

          <div className="row g-4">
            {[
              {
                icon: <Calendar size={22} />,
                title: "Regular Contact Classes & Eucharist",
                desc: "Periodic intensive contact seminars and community Eucharistic celebrations at designated regional diocesan centres."
              },
              {
                icon: <Monitor size={22} />,
                title: "Interactive Online Portal & Forums",
                desc: "Dedicated student platform at www.vjdepth.in with WhatsApp study circles and downloadable study material."
              },
              {
                icon: <BookOpen size={22} />,
                title: "Structured Mini Comprehensive Exams",
                desc: "Phase-end evaluations conducted locally nearer to candidates to consolidate biblical and doctrinal understanding."
              },
              {
                icon: <Award size={22} />,
                title: "Annual Seminar & Convocation",
                desc: "Formal graduation ceremony and academic seminar providing ongoing theological dialogue."
              },
              {
                icon: <Users size={22} />,
                title: "Yearly Laity Webinars",
                desc: "In-depth discussions on papal encyclicals, synodality documents, and pastoral issues with expert theologians."
              },
              {
                icon: <CheckCircle2 size={22} />,
                title: "Laity-Managed Magazine",
                desc: "Student-run creative journal to contextualize theology through articles, essays, and pastoral experiences."
              }
            ].map((feature, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="p-4 rounded-4 h-100 border" style={{ background: '#f8fafc', borderLeft: '4px solid #79031d' }}>
                  <div className="d-inline-flex p-2.5 rounded-3 mb-3" style={{ background: 'rgba(121,3,29,0.08)', color: '#79031d' }}>
                    {feature.icon}
                  </div>
                  <h5 className="fw-bold mb-2" style={{ color: '#1a2b56', fontSize: '1.05rem' }}>{feature.title}</h5>
                  <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
